import HttpApi from "./HttpApi";
import { DepositStatus } from "./TbtcApi";
/**
 * Extracts the Ethereum transaction hash from a subgraph event id. The subgraph
 * builds every event id as `<transactionHash>_<name>`, or
 * `<transactionHash>_<activityId>_<name>` where one transaction emits the same
 * event for several activities - the transaction hash is always the first
 * segment. See `Event` in `subgraph/schema.graphql`.
 *
 * @param eventId The event id returned by the subgraph.
 * @returns The Ethereum transaction hash.
 */
function getTransactionHashFromEventId(eventId) {
    return eventId.split("_")[0];
}
export function buildGetDepositsByOwnerQuery(owner) {
    return `
  query {
    deposits(
          where: {depositOwner_: {id: "0x${owner.identifierHex}"}}
      ) {
          id
          bitcoinTransactionId
          initialDepositAmount
          events(orderBy: timestamp, orderDirection: asc) {
            timestamp
            type
          }
          amountToDeposit
      }
  }`;
}
export function buildGetWithdrawalsByOwnerQuery(owner) {
    return `
  query {
    withdraws(
      where: {depositOwner_contains_nocase: "0x${owner.identifierHex}"}
    ) {
        id
        bitcoinTransactionId
        requestedAmount
        amountToRedeem
        amount
        destination
        events(orderBy: timestamp, orderDirection: asc) {
          id
          timestamp
          type
        }
      }
  }`;
}
/**
 * Class for integration with Acre Subgraph.
 */
export default class AcreSubgraphApi extends HttpApi {
    /**
     * @param depositOwnerId The deposit owner id as EVM-chain identifier.
     * @returns All deposits for a given depositor. Returns only initialized or
     *          finalized deposits that exist on-chain. They do not included
     *          queued deposits stored by the tBTC API.
     */
    async getDepositsByOwner(depositOwnerId) {
        const query = buildGetDepositsByOwnerQuery(depositOwnerId);
        const response = await this.postRequest("", { query }, { credentials: undefined });
        if (!response.ok) {
            throw new Error(`Could not get deposits by deposit owner: ${response.status}`);
        }
        const responseData = (await response.json());
        return responseData.data.deposits.map((deposit) => {
            const { bitcoinTransactionId: txHash, amountToDeposit, initialDepositAmount, events, id, } = deposit;
            // The subgraph indexes only initialized, finalized or migrated deposits.
            let status = DepositStatus.Initialized;
            if (events.some(({ type }) => type === "Finalized"))
                status = DepositStatus.Finalized;
            if (events.some(({ type }) => type === "Migrated"))
                status = DepositStatus.Migrated;
            const [initializedEvent, finalizedEvent] = events;
            const initializedAt = parseInt(initializedEvent.timestamp, 10);
            const finalizedAt = finalizedEvent
                ? parseInt(finalizedEvent.timestamp, 10)
                : undefined;
            return {
                depositKey: id,
                txHash,
                initialAmount: BigInt(initialDepositAmount),
                amountToDeposit: BigInt(amountToDeposit ?? 0),
                type: "deposit",
                status,
                initializedAt,
                finalizedAt,
            };
        });
    }
    async getWithdrawalsByOwner(depositOwnerId) {
        const query = buildGetWithdrawalsByOwnerQuery(depositOwnerId);
        const response = await this.postRequest("", { query }, { credentials: undefined });
        if (!response.ok) {
            throw new Error(`Could not get withdrawals by deposit owner: ${response.status}`);
        }
        const acreWithdrawals = (await response.json());
        return acreWithdrawals.data.withdraws.map((withdraw) => {
            const { id, events } = withdraw;
            const bitcoinTransactionId = withdraw.bitcoinTransactionId ?? undefined;
            const requestedAmount = BigInt(withdraw.requestedAmount);
            // The `amount` field from subgraph is available once the bridging process
            // on the tBTC side has finished. If it's not available let's use the
            // `amountToRedeem` field, available immediately after withdrawal request
            // initialization.
            const amount = withdraw.amount
                ? BigInt(withdraw.amount)
                : BigInt(withdraw.amountToRedeem);
            // Look the events up by type rather than by position: a withdrawal paid
            // out in tBTC never goes through the tBTC Bridge, so it has no
            // `Initialized` stage, and positionally its `Finalized` event would be
            // read as the initialization.
            const findEventTimestamp = (type) => {
                const event = events.find((e) => e.type === type);
                return event ? parseInt(event.timestamp, 10) : undefined;
            };
            const requestedEvent = events.find(({ type }) => type === "Requested");
            return {
                id,
                bitcoinTransactionId,
                amount,
                requestedAmount,
                destination: withdraw.destination === "Ethereum"
                    ? "ethereum"
                    : "bitcoin",
                ethereumTransactionId: requestedEvent
                    ? getTransactionHashFromEventId(requestedEvent.id)
                    : "",
                requestedAt: requestedEvent
                    ? parseInt(requestedEvent.timestamp, 10)
                    : 0,
                initializedAt: findEventTimestamp("Initialized"),
                finalizedAt: findEventTimestamp("Finalized"),
            };
        });
    }
}
//# sourceMappingURL=AcreSubgraphApi.js.map